import { createSelector } from 'reselect';
import get from 'lodash/get';
import permissionConstants from 'xm-constants/permissions';
import { hasPermission, hasAnyPermission } from 'utils/permissions';
import {
  incidentManagementMVPEnabled,
  isIncidentTypesEnabled,
  isIncidentTaskListsEnabled,
  isEverbridgeFlowDesignerApp,
  isServicesEnabled,
} from 'selectors/featureToggles';
import IconServices from 'resources/svg/icon-services.svg';

import { NavigationId } from 'Application/MainNav/constants';
import identity from 'lodash/identity';
import { getIsCompanySupervisor } from 'selectors/loggedInUser';
import getAdminNav from './admin';
import getDeveloperNav, {
  getFloodControlNavItem,
  getXAgentNavItem,
  getWorkflowsNavItem,
} from './developer';
import getGroupsNav from './groups';
import getIncidentsNav from './incidents';
import getReportsNav, { getSignalsReportNavItem } from './reports';
import getUsersNav from './users';
import {
  getSettingsNav,
  showSettingsInPlaceOfAdmin,
  showPlanOverviewInPlaceOfAdmin,
} from './settingsNav';
import {
  pruneItemsAndGroups,
  selectFeatureToggleMap,
  selectPermissions,
} from './util';

const {
  ABILITY_ACT_VIEW_DASHBOARDS,
  VIEW_MENU_MY_PROFILE,
  VIEW_MENU_ALERTS,
  VIEW_MENU_USERS,
  VIEW_MENU_GROUPS,
  VIEW_MENU_REPORTING,
  VIEW_MENU_MESSAGING,
  VIEW_MENU_ADMIN,
  VIEW_MENU_DEVELOPER,
  VIEW_MENUITEM_SERVICES,
} = permissionConstants;

const firstPlaceTo = subNav => get(subNav, [0, 'items', 0, 'to']);

export function getEverbridgeFlowDesignerNav(
  permissions,
  signalsReportNavItem,
  workflowsNavItem,
  floodControlNavItem,
  xAgentNavItem,
  adminNav,
  showSettings,
  incidentTypesEnabled,
  isCompanySupervisor,
) {
  const reportsTo = signalsReportNavItem?.to;
  const adminTo = firstPlaceTo(adminNav);
  const developerNav = pruneItemsAndGroups(
    [
      {
        id: 'NAV_WORKFLOWS',
        items: [workflowsNavItem],
      },
      {
        id: 'WORKFLOW_TOOLS',
        items: [floodControlNavItem, xAgentNavItem],
      },
    ],
    permissions,
  );
  const developerTo = firstPlaceTo(developerNav);

  const incidentsItems = [];
  if (incidentTypesEnabled) {
    incidentsItems.push({
      id: 'NAV_INCIDENT_TYPES',
      to: 'incidents/incident-types',
      selectorId: 'IncidentTypesPlace',
    });
  }
  const incidentsNav = pruneItemsAndGroups(
    [
      {
        id: 'NAV_MANAGE_INCIDENTS',
        items: incidentsItems,
      },
    ],
    permissions,
  );
  const incidentsTo = firstPlaceTo(incidentsNav);

  return [
    isCompanySupervisor &&
      incidentsTo && {
        id: NavigationId.INCIDENTS,
        key: 'NAV_INCIDENTS',
        to: incidentsTo,
        icon: 'icon-incidents',
        textCode: 'NAV_INCIDENTS',
        subNav: incidentsNav,
      },
    reportsTo &&
      hasPermission(permissions, VIEW_MENU_REPORTING) && {
        id: NavigationId.REPORTS,
        key: 'NAV_REPORTS',
        to: reportsTo,
        icon: 'icon-nav-reports',
        textCode: 'NAV_REPORTS',
      },
    developerTo &&
      hasPermission(permissions, VIEW_MENU_DEVELOPER) && {
        id: NavigationId.DEVELOPER,
        key: 'NAV_DEVELOPER',
        to: developerTo,
        icon: 'icon-hollow-workflows',
        textCode: 'NAV_WORKFLOWS',
        subNav: developerNav,
      },
    adminTo &&
      !showSettings &&
      hasPermission(permissions, VIEW_MENU_ADMIN) && {
        id: NavigationId.ADMIN,
        key: 'NAV_ADMIN',
        to: adminTo,
        icon: 'icon-hollow-cog',
        textCode: 'NAV_ADMIN',
        subNav: adminNav,
      },
  ].filter(identity);
}

export function getXMattersAppNav(
  permissions,
  homeLocation,
  incidentsNav,
  usersNav,
  groupsNav,
  reportsNav,
  adminNav,
  settingsNav,
  developerNav,
  incidentManagementFlag,
  isIncidentTypesFlag,
  isIncidentTaskListsFlag,
  showSettings,
  showPlanOverviewOnly,
  servicesFlag,
) {
  const hasDashboard = hasAnyPermission(
    permissions,
    ABILITY_ACT_VIEW_DASHBOARDS,
    VIEW_MENU_MY_PROFILE,
  );

  const reportsTo = firstPlaceTo(reportsNav);
  const adminTo = firstPlaceTo(adminNav);
  const settingsTo = firstPlaceTo(settingsNav);
  const developerTo = firstPlaceTo(developerNav);

  const messagingListTo = 'MessagingList';

  const navItems = [
    hasDashboard && {
      id: NavigationId.HOME,
      key: 'NAV_HOME',
      to: homeLocation,
      icon: 'icon-hollow-dashboard',
      textCode: 'NAV_DASHBOARD',
      legacyTextCode: 'NAV_HOME',
    },
    incidentManagementFlag === true && {
      id: NavigationId.INCIDENTS,
      key: 'NAV_INCIDENTS',
      to:
        isIncidentTypesFlag || isIncidentTaskListsFlag
          ? firstPlaceTo(incidentsNav) || 'incidents'
          : 'incidents',
      icon: 'icon-incidents',
      textCode: 'NAV_INCIDENTS',
      legacyTextCode: 'NAV_INCIDENTS',
      ...(isIncidentTypesFlag && { subNav: incidentsNav }),
      ...(isIncidentTaskListsFlag && { subNav: incidentsNav }),
    },
    servicesFlag === true &&
      hasPermission(permissions, VIEW_MENUITEM_SERVICES) && {
        id: NavigationId.SERVICES,
        key: 'NAV_SERVICES',
        to: 'services',
        IconComponent: IconServices,
        textCode: 'NAV_SERVICES',
        legacyTextCode: 'NAV_SERVICES',
      },
    hasPermission(permissions, VIEW_MENU_USERS) && {
      id: NavigationId.USERS,
      key: 'NAV_USERS',
      to: firstPlaceTo(usersNav) || 'users',
      icon: 'icon-hollow-user',
      textCode: 'NAV_USERS',
      subNav: usersNav,
    },
    hasPermission(permissions, VIEW_MENU_GROUPS) && {
      id: NavigationId.GROUPS,
      key: 'NAV_GROUPS',
      to: firstPlaceTo(groupsNav) || 'groups',
      icon: 'icon-hollow-people',
      textCode: 'NAV_GROUPS',
      subNav: groupsNav,
    },
    reportsTo &&
      hasPermission(permissions, VIEW_MENU_REPORTING) && {
        id: NavigationId.REPORTS,
        key: 'NAV_REPORTS',
        to: reportsTo,
        icon: 'icon-nav-reports',
        legacyIcon: 'icon-group',
        textCode: 'NAV_REPORTS',
        subNav: reportsNav,
      },
    hasPermission(permissions, VIEW_MENU_MESSAGING) && {
      id: NavigationId.MESSAGING,
      key: 'NAV_MESSAGING',
      to: messagingListTo,
      icon: 'icon-hollow-megaphone',
      textCode: 'NAV_MESSAGING',
    },
    adminTo &&
      !showSettings &&
      hasPermission(permissions, VIEW_MENU_ADMIN) && {
        id: NavigationId.ADMIN,
        key: 'NAV_ADMIN',
        to: adminTo,
        icon: 'icon-hollow-cog',
        textCode: 'NAV_ADMIN',
        subNav: adminNav,
      },
    settingsTo &&
      showSettings &&
      (hasPermission(permissions, VIEW_MENU_ADMIN) || showPlanOverviewOnly) && {
        id: NavigationId.ADMIN,
        key: 'NAV_ADMIN',
        to: settingsTo,
        icon: 'icon-hollow-cog',
        textCode: 'NAV_SETTINGS',
        subNav: showPlanOverviewOnly ? null : settingsNav,
      },
    developerTo &&
      hasPermission(permissions, VIEW_MENU_DEVELOPER) && {
        id: NavigationId.DEVELOPER,
        key: 'NAV_DEVELOPER',
        to: developerTo,
        icon: 'icon-hollow-workflows',
        textCode: 'NAV_WORKFLOWS',
        subNav: developerNav,
      },
  ];

  return navItems.filter(identity);
}

export const getAllowDashboards = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap => featureToggleMap.XM_DASHBOARDS === 'true',
);

export const getHomeLocationBase = (
  allowDashboards,
  isEverbridgeFlowDesigner,
  permissions,
) => {
  if (isEverbridgeFlowDesigner) {
    return 'WorkflowListPlace:/';
  }
  if (
    allowDashboards &&
    hasPermission(permissions, ABILITY_ACT_VIEW_DASHBOARDS)
  ) {
    return 'DashboardPlace:home';
  }
  return 'HomePagePlace:home';
};

export const getAlertsNavProps = permissions => {
  if (hasPermission(permissions, VIEW_MENU_ALERTS)) {
    const key = 'NAV_ALERTS';

    return {
      id: 'INBOX',
      key,
      icon: null,
      to: 'InboxPlace:none',
      legacyTextCode: 'NAV_INBOX',
      textCode: key,
    };
  }
  return false;
};

export const getHomeLocation = createSelector(
  [getAllowDashboards, isEverbridgeFlowDesignerApp, selectPermissions],
  getHomeLocationBase,
);

export const getAlertsNavItem = createSelector(
  [selectPermissions, selectFeatureToggleMap],
  getAlertsNavProps,
);

export const getXMattersAppNavItems = createSelector(
  [
    selectPermissions,
    getHomeLocation,
    getIncidentsNav,
    getUsersNav,
    getGroupsNav,
    getReportsNav,
    getAdminNav,
    getSettingsNav,
    getDeveloperNav,
    incidentManagementMVPEnabled,
    isIncidentTypesEnabled,
    isIncidentTaskListsEnabled,
    showSettingsInPlaceOfAdmin,
    showPlanOverviewInPlaceOfAdmin,
    isServicesEnabled,
  ],
  getXMattersAppNav,
);

export const getEverbridgeFlowDesignerAppNavItems = createSelector(
  [
    selectPermissions,
    getSignalsReportNavItem,
    getWorkflowsNavItem,
    getFloodControlNavItem,
    getXAgentNavItem,
    getAdminNav,
    showSettingsInPlaceOfAdmin,
    isIncidentTypesEnabled,
    getIsCompanySupervisor,
  ],
  getEverbridgeFlowDesignerNav,
);

export const getNavItems = state =>
  isEverbridgeFlowDesignerApp(state)
    ? getEverbridgeFlowDesignerAppNavItems(state)
    : getXMattersAppNavItems(state);
